import { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { messagePropType } from "app/utils/propTypes";
import "./TravellersRoomButton.scss";
import classNames from "classnames";
import { scrollToElement } from "app/utils/scroller";
import TravellersRoomForm from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomForm";
import {
	autoUpdate,
	FloatingPortal,
	offset,
	shift,
	useDismiss,
	useFloating,
	useFocus,
	useInteractions,
} from "@floating-ui/react";

const TravellersRoomButton = props => {
	const [open, setOpen] = useState(false);

	const {
		label,
		className,
		icon,
		initialValues,
		valueToDisplay,
		onConfirmation,
		id,
		stickyMode = false,
	} = props;

	const inputClassName = classNames(
		"travellers-room-button",
		"travellers-room-button--touched",
		className,
		{
			"travellers-room-button--with-icon": icon,
			"travellers-room-button--opened": open,
		}
	);

	const { context, x, y, strategy, refs } = useFloating({
		placement: "bottom",
		whileElementsMounted: autoUpdate,
		open,
		onOpenChange: setOpen,
		middleware: [offset(), shift()],
	});

	const handleValidation = useCallback(
		values => {
			setOpen(false);
			if (refs?.reference?.current) {
				scrollToElement({
					element: refs?.reference?.current,
					offset: -300,
					disableSmoothScroll: false,
				});
			}
			onConfirmation(values.occupancies);
		},
		[refs?.reference?.current]
	);

	const dismiss = useDismiss(context);
	// gere le click et le focus par clavier
	const focus = useFocus(context, {
		keyboardOnly: false,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, focus]);

	const handleClick = () => {
		if (refs?.floating?.current) {
			const floatingBottomPosition =
				refs?.floating?.current.getBoundingClientRect().y +
				refs?.floating?.current.offsetHeight;

			const windowHeight = window.outerHeight;

			if (floatingBottomPosition > windowHeight) {
				refs.floating.current.scrollIntoView({ behavior: "smooth", block: "end" });
			}
		}
	};
	return (
		<>
			<div
				className={inputClassName}
				ref={refs.setReference}
				id={id}
				tabIndex="0"
				{...getReferenceProps({})}
				onClick={handleClick}
			>
				<label className="travellers-room-button__label">{label}</label>
				<div className="travellers-room-button__input" data-testid="travellers-room-button">
					{icon && <div className="travellers-room-button__icon">{icon}</div>}
					{valueToDisplay}
				</div>
			</div>
			<FloatingPortal preserveTabOrder={true}>
				{open && (
					<div
						ref={refs.setFloating}
						className={classNames({
							"travellers-room-button__popover": true,
							"travellers-room-button__popover--sticky": stickyMode,
						})}
						{...getFloatingProps({
							style: {
								position: strategy,
								left: x + 90 ?? "",
								top: y ?? "",
							},
						})}
					>
						<div className="travellers-room-button__popover-body">
							<TravellersRoomForm
								initialValues={initialValues}
								handleValidation={handleValidation}
							/>
						</div>
					</div>
				)}
			</FloatingPortal>
		</>
	);
};

TravellersRoomButton.propTypes = {
	label: messagePropType,
	className: PropTypes.string,
	icon: PropTypes.element,
	valueToDisplay: PropTypes.element,
	onConfirmation: PropTypes.func,
	initialValues: PropTypes.array,
	id: PropTypes.string,
	stickyMode: PropTypes.bool,
};

TravellersRoomButton.defaultProps = {
	initialValues: [],
	onConfirmation: () => {},
};

export default memo(TravellersRoomButton);
